import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { V2_STUDIO_PATH } from '../../../lib/userGroups';
import { leerOnboardingEnLaBase } from '../../../lib/cuestionarioEnLaBase';
import { onboardingPendienteEnMemoria } from '../../../lib/cuestionarioObligatorio';
import { trackEvent } from '../../../lib/posthog';
import { ONBOARDING_V2 } from '../onboardingV2Routes';
import { leerLugarParaMarcas } from '../../../lib/lugarParaMarcas';
import { isPaidPlan } from '../../../lib/planCapabilities';
import SubscriptionLimitModal from '../../SubscriptionLimitModal';
import { enGuion } from '../../../lib/onboardingMarca/guion';
import { hayQueReiniciar, leerEstado, limpiarEstado } from '../../../lib/onboardingMarca/estado';
import { buscarMarca } from '../../../lib/onboardingMarca/marca';
import {
  decidirMarcaNueva,
  DESCRIBIR_MARCA,
  enDescribirMarca,
  enMarcaNueva,
  MARCA_NUEVA,
  NOMBRE_DEL_PASO_DE_DESCRIBIR_MARCA,
  NOMBRE_DEL_PASO_DE_MARCA_NUEVA,
  PASOS_DE_DESCRIBIR_MARCA,
  PASOS_DE_MARCA_NUEVA,
  retomaUnaMarcaDelFlujo,
} from '../../../lib/onboardingMarca/marcaNueva';
import { MarcoDelOnboarding } from './MarcoDelOnboarding';

const PASOS = [
  ONBOARDING_V2.marcaSetup,
  ONBOARDING_V2.setupPreguntas,
  ONBOARDING_V2.setupMarca,
  // El canal va justo después de la marca (ADR-2026-10-02-onboarding-v2-canal-despues-de-la-marca).
  ONBOARDING_V2.setupCanal,
  ONBOARDING_V2.setupPiezas,
  ONBOARDING_V2.setupListo,
];

/** Lo que dice la barra al lado de "Paso n de N". */
const NOMBRE_DEL_PASO: Record<(typeof PASOS)[number], string> = {
  [ONBOARDING_V2.marcaSetup]: 'Tu web',
  [ONBOARDING_V2.setupPreguntas]: 'Tu negocio',
  [ONBOARDING_V2.setupMarca]: 'Tu marca',
  [ONBOARDING_V2.setupCanal]: 'Canal de venta',
  [ONBOARDING_V2.setupPiezas]: 'Primeras piezas',
  [ONBOARDING_V2.setupListo]: 'Listo',
};

/**
 * Marco del onboarding de marca (ADR-2026-09-28-onboarding-v2-etapa-1): arriba, en qué
 * paso va y cómo se llama, sobre el fondo de la pantalla; cada pantalla entra con un
 * fundido. Sin logo ni "Volver" (ADR-2026-10-02-onboarding-v2-pasos-integrados): el
 * paso 1 ya no desloguea desde acá.
 */
export function OnboardingMarcaLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // "Añadir nueva marca" usa este mismo marco con sus tres pasos (ADR-2026-10-02-marca-nueva-como-el-onboarding).
  const nueva = enMarcaNueva(pathname);
  // "Describí tu marca" desde la home, también con este marco y sus tres pasos
  // (ADR-2026-10-19-describir-la-marca-con-el-onboarding-nuevo). No pide plan multimarca: es la
  // marca que ya tiene.
  const describir = enDescribirMarca(pathname);
  const pasos: readonly string[] = nueva ? PASOS_DE_MARCA_NUEVA : describir ? PASOS_DE_DESCRIBIR_MARCA : PASOS;
  const nombres: Record<string, string> = nueva
    ? NOMBRE_DEL_PASO_DE_MARCA_NUEVA
    : describir
      ? NOMBRE_DEL_PASO_DE_DESCRIBIR_MARCA
      : NOMBRE_DEL_PASO;
  const paso = Math.max(0, pasos.indexOf(pathname));
  const enTablero = pathname === ONBOARDING_V2.setupMarca || pathname === MARCA_NUEVA.marca || pathname === DESCRIBIR_MARCA.marca;

  // Quien YA terminó el onboarding y cae en el primer paso sin nada a medio hacer —volver
  // con "atrás" desde Listo (que ya olvidó lo guardado), un link viejo, un rebote de otra
  // causa— va a la app en vez de empezar de cero: rehacerlo le creaba otra marca y le
  // pisaba el nombre y el logo de la empresa
  // (ADR-2026-10-20-el-onboarding-no-rebota-con-una-lectura-vieja).
  //
  // SÓLO en la ruta del onboarding: "Añadir nueva marca" usa este mismo marco, y ahí la
  // persona tiene SIEMPRE el rubro real — sin este corte, nadie podría sumar una marca.
  // Lo decide la base, no el flag; si no se puede leer, no se mueve a nadie.
  useEffect(() => {
    if (pathname !== ONBOARDING_V2.marcaSetup || nueva || enGuion()) return;
    if (leerEstado(null)) return;
    let activo = true;
    // Desde ADR-2026-10-31-onboarding-corto-para-la-prueba, "ya terminó" es "no debe ningún
    // onboarding": quien viene del cuestionario corto tiene el rubro real y, si pagó, todavía
    // debe éste. Mirando sólo el rubro lo echaba al estudio y el guardia lo devolvía: un loop.
    void leerOnboardingEnLaBase({ recienPago: onboardingPendienteEnMemoria() === 'marca' }).then((enLaBase) => {
      if (!activo || enLaBase !== null) return;
      trackEvent('onboarding_ya_completo', { ruta: pathname });
      navigate(V2_STUDIO_PATH, { replace: true });
    });
    return () => {
      activo = false;
    };
  }, [pathname, nueva, navigate]);

  // Sumar una marca depende del lugar que deja el plan, como en la pantalla vieja
  // (ADR-2026-10-27-planes-de-equipo-en-el-front). Sin lugar, la MISMA ventana que en el
  // selector de marca —antes saltaba a los planes sin decir por qué—: un plan pago ve su tope
  // y "Ver planes"; gratis, que sumar marcas es de los planes pagos. Al cerrarla, a la home:
  // sin lugar no hay alta que seguir. Si se recarga a mitad del flujo, la marca de este flujo
  // ya ocupa su lugar y no se frena (`retomaUnaMarcaDelFlujo`).
  // Con facturación por marca, sólo del dueño: los demás vuelven al estudio
  // (ADR-2026-10-28-marca-nueva-con-pago); y no hay tope, porque cada marca paga su plan
  // (ADR-2026-10-28-facturacion-por-marca-sin-tope-de-marcas).
  const [sinLugar, setSinLugar] = useState<{ tope: number | null; pago: boolean } | null>(null);
  useEffect(() => {
    if (!nueva || enGuion()) return;
    // La ruta con la que se montó el marco: el control corre una vez, al entrar al flujo.
    if (retomaUnaMarcaDelFlujo(window.location.pathname, leerEstado(null))) return;
    let activo = true;
    void decidirMarcaNueva().then(async (decision) => {
      if (!activo) return;
      if (decision === 'solo_admin') {
        navigate(V2_STUDIO_PATH, { replace: true });
        return;
      }
      const lugar = await leerLugarParaMarcas();
      if (activo && !lugar.puedeSumar) setSinLugar({ tope: lugar.tope, pago: isPaidPlan(lugar.plan) });
    });
    return () => {
      activo = false;
    };
  }, [nueva]);
  const cerrarSinLugar = () => {
    setSinLugar(null);
    // "Ver planes" ya navegó antes de cerrar: no se pisa ese destino.
    if (enMarcaNueva(window.location.pathname)) navigate('/', { replace: true });
  };

  // Sin sesión, a la bienvenida. Con sesión, el onboarding guardado tiene que ser de ESTA
  // cuenta: si lo dejó otra (una sesión que venció sin pasar por el logout, y después entró
  // otra persona), se tira y se arranca de cero, en vez de seguir con su empresa y su marca
  // (ADR-2026-10-07-el-onboarding-guardado-se-valida-contra-la-sesion).
  useEffect(() => {
    let activo = true;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!activo) return;
      if (!data.session) return navigate(ONBOARDING_V2.bienvenida, { replace: true });
      const guardado = leerEstado(null);
      if (!guardado || enGuion()) return;
      const { data: perfil, error } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', data.session.user.id)
        .maybeSingle();
      if (!activo) return;
      const companyIdDeLaSesion = error ? undefined : ((perfil as { id_company?: string | null } | null)?.id_company ?? null);
      // Si la empresa no se pudo leer, decide la marca guardada: la de otra cuenta no existe
      // para esta sesión (ADR-2026-10-07-una-marca-que-no-existe-reinicia-el-onboarding).
      const marca =
        companyIdDeLaSesion === undefined && guardado.brandId ? (await buscarMarca(guardado.brandId)).estado : undefined;
      if (!activo || !hayQueReiniciar({ guardado, companyIdDeLaSesion, marca })) return;
      limpiarEstado();
      navigate(nueva ? MARCA_NUEVA.link : describir ? DESCRIBIR_MARCA.link : ONBOARDING_V2.marcaSetup, { replace: true });
    })();
    return () => {
      activo = false;
    };
    // Se valida una vez al entrar al flujo: el marco queda montado entre paso y paso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <MarcoDelOnboarding
      ancho={enTablero}
      pasos={{
        actual: paso,
        total: pasos.length,
        nombre: nombres[pasos[paso]],
        // Adentro de la app hay que poder irse: la marca queda sin sumar.
        accion: nueva ? (
          <button type="button" onClick={() => navigate('/', { replace: true })} className="om-salir">
            Salir
          </button>
        ) : undefined,
      }}
      despues={
        <SubscriptionLimitModal
          isOpen={sinLugar !== null}
          onClose={cerrarSinLugar}
          type={sinLugar?.pago ? 'tope-de-marcas' : 'multi-brand-required'}
          tope={sinLugar?.tope ?? null}
        />
      }
    >
      <main key={pathname} className="om-pantalla">
        <Outlet />
      </main>
    </MarcoDelOnboarding>
  );
}
